// src/pages/PostLoginRouter.js
import React from "react";
import { useNavigate } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { Box, Stack, Typography, CircularProgress } from "@mui/material";

import SubscriptionActivationGate from "../components/onboarding/SubscriptionActivationGate";

// Use your deployed CF URL as a safe fallback
const ORG_CONTEXT_URL =
  process.env.REACT_APP_GET_ORG_CONTEXT_URL ||
  "https://getorgcontext-774073159618.us-central1.run.app";

const DISMISS_URL =
  process.env.REACT_APP_DISMISS_BILLING_ACTIVATION_URL ||
  "https://dismissbillingactivation-774073159618.us-central1.run.app";

// Optional: honor build-time stripe mode (defaults to live if not specified)
const STRIPE_MODE = (process.env.REACT_APP_STRIPE_MODE || "live").toLowerCase();
const MODE_KEY = STRIPE_MODE === "test" ? "test" : "live";

const safeString = (v) => (typeof v === "string" ? v : "");

// Minimum time to keep splash visible (prevents “flash” on fast responses)
const MIN_SPLASH_MS = 650;

const PostLoginSplash = () => (
  <Box
    sx={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      bgcolor: "background.default",
      px: 2,
    }}
  >
    <Stack spacing={2.25} alignItems="center" textAlign="center">
      <Box
        component="img"
        src="https://storage.googleapis.com/kinova/Kinova_transparent.png"
        alt="Kinova"
        sx={{
            height: { xs: 96, sm: 120, md: 140 },
            width: "auto",
            maxWidth: "85vw",
            objectFit: "contain",
            display: "block",
            }}
      />

      <CircularProgress size={28} />

      <Typography variant="body2" color="text.secondary">
        Preparing your workspace…
      </Typography>
    </Stack>
  </Box>
);

// Robust timestamp parser
const toDateSafe = (v) => {
  if (!v) return null;

  if (typeof v?.toDate === "function") {
    try {
      const d = v.toDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
    } catch {
      return null;
    }
  }

  const seconds =
    (typeof v?._seconds === "number" && v._seconds) ||
    (typeof v?.seconds === "number" && v.seconds) ||
    null;

  const nanos =
    (typeof v?._nanoseconds === "number" && v._nanoseconds) ||
    (typeof v?.nanoseconds === "number" && v.nanoseconds) ||
    0;

  if (seconds !== null) {
    const ms = seconds * 1000 + Math.floor(nanos / 1e6);
    const d = new Date(ms);
    return !Number.isNaN(d.getTime()) ? d : null;
  }

  if (typeof v === "string") {
    const d = new Date(v);
    return !Number.isNaN(d.getTime()) ? d : null;
  }

  if (typeof v === "number") {
    const d = new Date(v);
    return !Number.isNaN(d.getTime()) ? d : null;
  }

  return null;
};

const callAuthedJson = async (url, payload = {}) => {
  if (!url) throw new Error("Missing API URL.");
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("Not authenticated.");

  const token = await user.getIdToken();

  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json?.error || "Request failed.");
  return json;
};

const isSubscriptionActive = (billingSummary) => {
  if (!billingSummary) return false;

  const statusByMode = billingSummary?.subscriptionStatusByMode || {};
  const status =
    safeString(statusByMode?.[MODE_KEY]) ||
    safeString(billingSummary?.subscriptionStatus);

  const ACTIVE_SET = new Set(["active", "trialing", "past_due", "unpaid"]);
  return ACTIVE_SET.has(status);
};

const getDismissState = (org) => {
  const act = org?.billingActivation || {};
  const dismissUntil = toDateSafe(act?.dismissUntil);

  const now = new Date();
  const isDismissed = !!dismissUntil && dismissUntil.getTime() > now.getTime();

  return {
    isDismissed,
    dismissUntil, // Date | null
    hadDismissBefore: !!act?.dismissUntil,
  };
};

const routeByRoles = (rolesCsv) => {
  const roles = String(rolesCsv || "")
    .split(",")
    .map((r) => r.trim())
    .filter(Boolean);

  if (roles.includes("cereset")) return "/documents/dashboard";
  if (roles.includes("admin") && roles.includes("user")) return "/admin";
  return "/dashboard";
};

export default function PostLoginRouter() {
  const navigate = useNavigate();

  const [loading, setLoading] = React.useState(true);
  const [gateOpen, setGateOpen] = React.useState(false);
  const [allowDismiss, setAllowDismiss] = React.useState(true);

  const navigateToRoleHome = React.useCallback(() => {
    const rolesCsv = sessionStorage.getItem("userRole") || "";
    const next = routeByRoles(rolesCsv);
    navigate(next, { replace: true });
  }, [navigate]);

  const decide = React.useCallback(async () => {
    setLoading(true);
    const started = Date.now();

    const finishWithMinSplash = async () => {
      const elapsed = Date.now() - started;
      const remaining = MIN_SPLASH_MS - elapsed;
      if (remaining > 0) {
        await new Promise((r) => setTimeout(r, remaining));
      }
    };

    try {
      const result = await callAuthedJson(ORG_CONTEXT_URL);
      const org = result?.org || {};
      const billingSummary = result?.billingSummary || null;

      const active = isSubscriptionActive(billingSummary);

      if (active) {
        await finishWithMinSplash();
        navigateToRoleHome();
        return;
      }

      const { isDismissed, dismissUntil, hadDismissBefore } = getDismissState(org);

      // If dismissed within window -> no gate, proceed to role home.
      if (isDismissed) {
        await finishWithMinSplash();
        navigateToRoleHome();
        return;
      }

      // If dismissal expired -> show gate again and remove dismiss button.
      const dismissExpired =
        hadDismissBefore && !!dismissUntil && dismissUntil.getTime() <= Date.now();

      setAllowDismiss(!dismissExpired);

      await finishWithMinSplash();
      setGateOpen(true);
    } catch (e) {
      await finishWithMinSplash();
      navigateToRoleHome();
    } finally {
      setLoading(false);
    }
  }, [navigateToRoleHome]);

  React.useEffect(() => {
    decide();
  }, [decide]);

  const onActivate = () => {
    setGateOpen(false);
    navigate("/settings/billing", { replace: true });
  };

  const onDismiss = async () => {
    if (!allowDismiss) return;

    try {
      await callAuthedJson(DISMISS_URL, {});
    } catch {
      // fail open
    } finally {
      setGateOpen(false);
      navigateToRoleHome();
    }
  };

  if (loading) return <PostLoginSplash />;

  if (gateOpen) {
    return (
      <SubscriptionActivationGate
        onActivate={onActivate}
        onDismiss={allowDismiss ? onDismiss : undefined}
        hideDismiss={!allowDismiss}
      />
    );
  }

  return null;
}